import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api"; // axios instance (with token)

/* ============================
   🚗 CREATE VEHICLE
============================ */
export const createVehicle = createAsyncThunk(
    "vehicle/create",
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await api.post("/vehicles/create", payload);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);


/* ============================
   📦 GET ALL VEHICLES
============================ */
export const fetchVehicles = createAsyncThunk(
    "vehicle/fetchAll",
    async (params = {}, { rejectWithValue }) => {
        try {
            const { data } = await api.get("/vehicles/all", { params });
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);


/* ============================
   🔍 GET SINGLE VEHICLE
============================ */
export const fetchSingleVehicle = createAsyncThunk(
    "vehicle/fetchSingle",
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/vehicles/single/${id}`);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);


/* ============================
   ✏️ UPDATE VEHICLE
============================ */
export const updateVehicle = createAsyncThunk(
    "vehicle/update",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const { data } = await api.put(`/vehicles/update/${id}`, payload);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

/* ============================
   🗑️ DELETE VEHICLE
============================ */
export const deleteVehicle = createAsyncThunk(
    "vehicle/delete",
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/vehicles/delete/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

/* ============================
   🚘 VEHICLE SLICE
============================ */
const vehicleSlice = createSlice({
    name: "vehicle",
    initialState: {
        vehicles: [],
        selectedVehicle: null,
        loading: false,
        error: null,
        success: false,
    },

    reducers: {
        clearVehicleState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
        },
        clearSelectedVehicle: (state) => {
            state.selectedVehicle = null;
        },
    },

    extraReducers: (builder) => {
        builder

            /* ===== CREATE ===== */
            .addCase(createVehicle.pending, (state) => {
                state.loading = true;
            })
            .addCase(createVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.vehicles.push(action.payload);
            })
            .addCase(createVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== GET ALL ===== */
            .addCase(fetchVehicles.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchVehicles.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicles = action.payload;
            })
            .addCase(fetchVehicles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== GET SINGLE ===== */
            .addCase(fetchSingleVehicle.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSingleVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedVehicle = action.payload;
            })
            .addCase(fetchSingleVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== UPDATE ===== */
            .addCase(updateVehicle.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.vehicles = state.vehicles.map((v) =>
                    v._id === action.payload._id ? action.payload : v
                );
            })
            .addCase(updateVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== DELETE ===== */
            .addCase(deleteVehicle.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.vehicles = state.vehicles.filter(
                    (v) => v._id !== action.payload
                );
            })
            .addCase(deleteVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearVehicleState,
    clearSelectedVehicle,
} = vehicleSlice.actions;

export default vehicleSlice.reducer;
